<template>
    <div v-if="project" class="space-y-4">
        <FlexibleCard headline-id="identifiers" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('identifiers')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('identifiers')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('identifiers')"
                    @enter-edit-mode="onEnterEditMode('identifiers', $t('identifiers'))"
                    @leave-edit-mode="leaveEditMode('identifiers')"
                    @discard-changes="discardChanges('identifiers')"
                    @save="saveForm('identifiers')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('project-base-data') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectIdentifiersForm
                        v-if="showForm('identifiers')"
                        ref="projectIdentifiersFormRef"
                        :project-id="project.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('name-short')"
                            :text="valueToString(project.nameShort)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('name-long')"
                            :text="valueToString(project.nameLong)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('subtitle')"
                            :text="valueToString(project.subtitle)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('execution-year')"
                            :text="valueToString(project.executionYear?.toString())"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('description')"
                            :text="valueToString(project.description)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="typeDefinitions" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('typeDefinitions')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('typeDefinitions')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('typeDefinitions')"
                    @enter-edit-mode="onEnterEditMode('typeDefinitions', $t('type-definition'))"
                    @leave-edit-mode="leaveEditMode('typeDefinitions')"
                    @discard-changes="discardChanges('typeDefinitions')"
                    @save="saveForm('typeDefinitions')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('type-definition') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectTypeDefinitionForm
                        v-if="showForm('typeDefinitions')"
                        ref="projectTypeDefinitionsFormRef"
                        :project-id="project.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('project-category')"
                            :text="valueToString(project.category)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-type')"
                            :text="valueToString(project.type)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="disturbance" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('disturbance')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('disturbance')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('disturbance')"
                    @enter-edit-mode="onEnterEditMode('disturbance', $t('disturbance'))"
                    @leave-edit-mode="leaveEditMode('disturbance')"
                    @discard-changes="discardChanges('disturbance')"
                    @save="saveForm('disturbance')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('disturbance') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectDisturbanceForm
                        v-if="showForm('disturbance')"
                        ref="projectDisturbanceFormRef"
                        :project-id="project.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('project-disturbance-noise')"
                            :text="valueToString(project.disturbanceNoise)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-disturbance-dust')"
                            :text="valueToString(project.disturbanceDust)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-disturbance-other')"
                            :text="valueToString(project.disturbanceOther)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { $t } from '@/plugins/fluent';
import { useProjectInjection } from '@/project/composables/useProjectInjection';
import ProjectDisturbanceForm from '@/project/forms/project/ProjectDisturbanceForm.vue';
import ProjectIdentifiersForm from '@/project/forms/project/ProjectIdentifiersForm.vue';
import ProjectTypeDefinitionForm from '@/project/forms/project/ProjectTypeDefinitionForm.vue';
import { useHeadSafe } from '@unhead/vue';
import { useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const { project } = useProjectInjection();

const {
    activeCard,
    getCardStatus,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    resetActiveCard,
    onEnterEditMode,
} = useActiveCard();

const projectIdentifiersFormRef = useTemplateRef('projectIdentifiersFormRef');
const projectTypeDefinitionFormRef = useTemplateRef('projectTypeDefinitionsFormRef');
const projectDisturbanceFormRef = useTemplateRef('projectDisturbanceFormRef');
const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();
register('identifiers', projectIdentifiersFormRef);
register('typeDefinitions', projectTypeDefinitionFormRef);
register('disturbance', projectDisturbanceFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm($t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});

useHeadSafe({ title: () => `${$t('master-data')} - ${project.value?.nameShort} - ${$t('project-wiki')}` });
</script>
